import React, { useState, useEffect } from 'react';
import { 
  Button, 
  FormGroup, 
  InputGroup, 
  Callout, 
  Intent, 
  Card, 
  Divider,
  Icon,
  Switch,
  Tag,
  Spinner,
  Dialog,
  Classes
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { observer } from 'mobx-react-lite';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import GithubIntegration from '../integrations/GithubIntegration';
import SlackIntegration from '../integrations/SlackIntegration';
import AvatarUpload from '../profile/AvatarUpload';
import api from '../../services/api';
import BackendLogsViewer, { logCollector, LogEntry } from '../BackendLogsViewer/BackendLogsViewer';
import './SettingsPage.css';

interface StatusMessage {
  text: string;
  intent: Intent;
}

const SettingsPage: React.FC = observer(() => {
  const stytch = useStytchB2BClient();
  const appStore = useAppStore();
  const { auth } = appStore;
  const user = auth.user;
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('profile');
  
  // Admin tab state
  const [members, setMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Profile form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [cursorColor, setCursorColor] = useState('#4A9EFF');
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [desktopNotifications, setDesktopNotifications] = useState(false);

  // Load user data on mount
  useEffect(() => {
    if (user) {
      const nameParts = (user.name || '').split(' ');
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
      setEmail(user.email || '');
      // Load cursor color from backend
      loadCursorColor();
    }
  }, [user]);

  const loadCursorColor = async () => {
    try {
      // Use /users/profile endpoint instead of /api/auth/me
      const response = await api.get('/users/profile');
      if (response.data?.cursorColor) {
        setCursorColor(response.data.cursorColor);
      }
    } catch (error) {
      console.error('Failed to load cursor color:', error);
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setStatusMessage(null);

    try {
      const response = await api.patch(
        `${import.meta.env.VITE_BACKEND_URL || ''}/users/profile`,
        {
          firstName,
          lastName,
          jobTitle,
          cursorColor,
        },
        {
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
          },
        }
      );

      // Update local auth store
      if (response.data) {
        auth.setUser({
          ...user!,
          name: `${firstName} ${lastName}`,
        });
      }

      // Update presence store cursor color
      if (user?.id) {
        appStore.onlinePresence.updateUserCursorColor(user.id, cursorColor);
      }

      setStatusMessage({
        text: 'Profile updated successfully!',
        intent: Intent.SUCCESS
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      setStatusMessage({
        text: 'Failed to save profile. Please try again.',
        intent: Intent.DANGER
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotifications = () => {
    // TODO: Save to backend API instead of localStorage
    // localStorage.setItem('emailNotifications', emailNotifications.toString());
    // localStorage.setItem('desktopNotifications', desktopNotifications.toString());
    
    setStatusMessage({
      text: 'Notification preferences saved!',
      intent: Intent.SUCCESS
    });
  };

  // Fetch members when admin tab is opened
  useEffect(() => {
    if (activeTab === 'admin' && auth.organization?.id) {
      fetchMembers();
    }
  }, [activeTab, auth.organization?.id]);

  const fetchMembers = async () => {
    console.log('🔍 [fetchMembers] Starting...');
    console.log('🔍 [fetchMembers] auth.organization:', auth.organization);
    console.log('🔍 [fetchMembers] auth.organization?.id:', auth.organization?.id);
    console.log('🔍 [fetchMembers] auth.organization?.stytchOrganizationId:', auth.organization?.stytchOrganizationId);
    
    if (!auth.organization?.id) {
      console.error('❌ [fetchMembers] No organization ID!');
      return;
    }
    
    setLoadingMembers(true);
    try {
      console.log(`📋 Fetching members for org: ${auth.organization.id}`);
      const response = await api.get(`/organizations/${auth.organization.id}/members`);
      const fetchedMembers = response.data.members || [];
      
      console.log(`✅ Fetched ${fetchedMembers.length} members:`);
      fetchedMembers.forEach((m: any) => {
        const statusEmoji = m.status === 'pending' ? '⏳' : m.status === 'active' ? '✓' : '?';
        console.log(`  ${statusEmoji} ${m.email}: status=${m.status}, role=${m.role}`);
      });
      
      setMembers(fetchedMembers);
    } catch (error: any) {
      console.error('❌ Failed to fetch members:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail || !auth.organization?.id) return;
    setInviting(true);
    try {
      console.log(`📧 Inviting ${inviteEmail} to organization via BACKEND...`);
      
      // Call backend to send invite
      // Backend has stytchOrganizationId and will call Stytch with correct params
      const response = await api.post(`/organizations/${auth.organization.id}/invite`, {
        email: inviteEmail,
        firstName: inviteFirstName,
        lastName: inviteLastName,
        role: inviteRole,
      });
      
      console.log('✅ Backend invite response:', response.data);
      
      // Clear form and close dialog
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteRole('member');
      setInviteDialogOpen(false);
      
      // Refresh members list to show the new pending invite
      console.log('🔄 Refreshing members list after invite...');
      await fetchMembers();
      
      setStatusMessage({ 
        text: `Invitation sent to ${inviteEmail}! They will receive an email with a link to join.`, 
        intent: Intent.SUCCESS 
      });
    } catch (error: any) {
      console.error('❌ Failed to invite member:', error);
      setStatusMessage({ 
        text: error.response?.data?.message || 'Failed to invite member.', 
        intent: Intent.DANGER 
      });
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (memberId: string) => {
    if (!auth.organization?.id) return;
    try {
      await api.post(`/organizations/${auth.organization.id}/members/${memberId}/resend-invite`);
      setStatusMessage({ text: 'Invite resent successfully!', intent: Intent.SUCCESS });
    } catch (error: any) {
      console.error('Failed to resend invite:', error);
      setStatusMessage({ text: 'Failed to resend invite.', intent: Intent.DANGER });
    }
  };

  const handleUpdateMemberRole = async (memberId: string, newRole: string) => {
    if (!auth.organization?.id) return;
    try {
      await api.patch(`/organizations/${auth.organization.id}/members/${memberId}/role`, { role: newRole });
      fetchMembers();
      setStatusMessage({ text: 'Role updated!', intent: Intent.SUCCESS });
    } catch (error) {
      setStatusMessage({ text: 'Failed to update role.', intent: Intent.DANGER });
    }
  };

  const handleDeleteMember = (member: any) => {
    setMemberToDelete(member);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteMember = async () => {
    if (!memberToDelete || !auth.organization?.id) return;
    
    setDeleting(true);
    try {
      await api.delete(`/organizations/${auth.organization.id}/members/${memberToDelete.id}`);
      setStatusMessage({ 
        text: `${memberToDelete.email} has been removed from the organization`, 
        intent: Intent.SUCCESS 
      });
      fetchMembers(); // Refresh member list
      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
    } catch (error: any) {
      console.error('❌ Failed to remove member:', error);
      const errorMsg = error.response?.data?.message || 'Failed to remove member';
      setStatusMessage({ text: errorMsg, intent: Intent.DANGER });
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreMember = async (memberId: string) => {
    if (!auth.organization?.id) return;
    
    try {
      await api.post(`/organizations/${auth.organization.id}/members/${memberId}/restore`);
      setStatusMessage({ text: 'Member restored successfully!', intent: Intent.SUCCESS });
      fetchMembers(); // Refresh member list
    } catch (error: any) {
      console.error('❌ Failed to restore member:', error);
      setStatusMessage({ text: 'Failed to restore member', intent: Intent.DANGER });
    }
  };

  const getInitials = () => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || email?.[0]?.toUpperCase() || '?';
  };

  return (
    <div className="settings-interface">
      {/* Settings Sidebar */}
      <div className="settings-sidebar">
        <div className="settings-sidebar-header">
          <h3>Settings</h3>
        </div>
        <div className="settings-nav">
          <Button
            className={`settings-nav-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
            icon={IconNames.USER}
            text="Profile"
            fill
            minimal
            large
            alignText="left"
          />
          <Button
            className={`settings-nav-button ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
            icon={IconNames.NOTIFICATIONS}
            text="Notifications"
            fill
            minimal
            large
            alignText="left"
          />
          <Button
            className={`settings-nav-button ${activeTab === 'integrations' ? 'active' : ''}`}
            onClick={() => setActiveTab('integrations')}
            icon={IconNames.LINK}
            text="Integrations"
            fill
            minimal
            large
            alignText="left"
          />
          <Button
            className={`settings-nav-button ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
            icon={IconNames.COG}
            text="Account"
            fill
            minimal
            large
            alignText="left"
          />
          <Button
            className={`settings-nav-button ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
            icon={IconNames.PEOPLE}
            text="Team Admin"
            fill
            minimal
            large
            alignText="left"
          />
          {/* Debug tab - only show for admins */}
          {(user?.role === 'admin' || user?.role === 'owner') && (
            <Button
              className={`settings-nav-button ${activeTab === 'debug' ? 'active' : ''}`}
              onClick={() => setActiveTab('debug')}
              icon={IconNames.CONSOLE}
              text="Debug"
              fill
              minimal
              large
              alignText="left"
            />
          )}
        </div>
      </div>

      {/* Settings Content */}
      <div className="settings-content">
        {statusMessage && (
          <Callout 
            intent={statusMessage.intent} 
            icon={statusMessage.intent === Intent.SUCCESS ? IconNames.TICK : IconNames.ERROR}
            className="settings-alert"
          >
            {statusMessage.text}
          </Callout>
        )}

        {activeTab === 'profile' && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h2>Profile Information</h2>
              <p>Update your personal information and avatar</p>
            </div>
            <Card className="settings-card">
              <div className="profile-content">
                {/* Avatar Upload Section */}
                <AvatarUpload
                  currentAvatarUrl={user?.avatar}
                  userId={user?.id || ''}
                  onAvatarUpdated={async (newAvatarUrl) => {
                    // Trigger auth check to reload full user profile
                    await auth.checkAuth();
                    
                    // Also update UserStore if available
                    if (appStore?.user && user?.id) {
                      appStore.user.updateUserAvatar(user.id, newAvatarUrl);
                    }
                  }}
                />

                <Divider style={{ margin: '24px 0' }} />

                <div className="profile-form">
                  <div className="form-row">
                    <FormGroup label="First Name" labelInfo="(required)">
                      <InputGroup
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Enter your first name"
                      />
                    </FormGroup>

                    <FormGroup label="Last Name" labelInfo="(required)">
                      <InputGroup
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Enter your last name"
                      />
                    </FormGroup>
                  </div>

                  <div className="form-row">
                    <FormGroup 
                      label="Cursor Color" 
                      helperText="Your cursor color in collaborative sessions"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <input
                          type="color"
                          value={cursorColor}
                          onChange={(e) => setCursorColor(e.target.value)}
                          aria-label="Select cursor color"
                          style={{
                            width: 80,
                            height: 38,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 4,
                            cursor: 'pointer',
                            background: 'transparent'
                          }}
                        />
                        <InputGroup
                          value={cursorColor}
                          onChange={(e) => setCursorColor(e.target.value)}
                          placeholder="#4A9EFF"
                          maxLength={7}
                          style={{ maxWidth: 120 }}
                        />
                        <Tag
                          large
                          style={{
                            background: cursorColor,
                            color: '#fff',
                            border: 'none'
                          }}
                        >
                          Preview
                        </Tag>
                      </div>
                    </FormGroup>
                  </div>

                  <div className="form-row">
                    <FormGroup label="Email" labelInfo="(read-only)">
                      <InputGroup
                        value={email}
                        disabled
                        placeholder="Your email address"
                      />
                    </FormGroup>

                    <FormGroup label="Job Title">
                      <InputGroup
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        placeholder="e.g. Software Engineer"
                      />
                    </FormGroup>
                  </div>
                </div>
              </div>

              <Divider />
              <div className="card-actions">
                <Button 
                  intent={Intent.PRIMARY}
                  icon={IconNames.FLOPPY_DISK}
                  text="Save Profile"
                  onClick={handleSaveProfile}
                  loading={isSaving}
                  disabled={!firstName || !lastName}
                />
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h2>Notification Preferences</h2>
              <p>Manage how you receive notifications and updates</p>
            </div>
            <Card className="settings-card">
              <div className="notification-item">
                <div className="notification-info">
                  <div className="notification-label">Email Notifications</div>
                  <div className="notification-desc">Receive updates about comments and mentions via email</div>
                </div>
                <Switch
                  checked={emailNotifications}
                  onChange={(e) => setEmailNotifications((e.target as HTMLInputElement).checked)}
                  large
                />
              </div>

              <Divider />

              <div className="notification-item">
                <div className="notification-info">
                  <div className="notification-label">Desktop Notifications</div>
                  <div className="notification-desc">Show browser notifications for real-time updates</div>
                </div>
                <Switch
                  checked={desktopNotifications}
                  onChange={(e) => setDesktopNotifications((e.target as HTMLInputElement).checked)}
                  large
                />
              </div>

              <Divider />
              <div className="card-actions">
                <Button 
                  intent={Intent.PRIMARY}
                  icon={IconNames.FLOPPY_DISK}
                  text="Save Preferences"
                  onClick={handleSaveNotifications}
                />
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'integrations' && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h2>Integrations</h2>
              <p>Connect external services and tools</p>
            </div>
            <div className="integrations-grid">
              <Card className="settings-card integration-card">
                <div className="integration-header">
                  <Icon icon={IconNames.GIT_REPO} className="integration-icon" />
                  <h3>GitHub</h3>
                </div>
                <Divider />
                <GithubIntegration 
                  organizationId={auth.organization?.id || ''} 
                  onConnectionChange={() => {}}
                />
              </Card>

              <Card className="settings-card integration-card">
                <div className="integration-header">
                  <Icon icon={IconNames.CHAT} className="integration-icon" />
                  <h3>Slack</h3>
                </div>
                <Divider />
                <SlackIntegration 
                  organizationId={auth.organization?.id || ''} 
                  onConnectionChange={() => {}}
                />
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'account' && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h2>Account Settings</h2>
              <p>Manage your account details and security</p>
            </div>
            <Card className="settings-card">
              <div className="account-info-grid">
                <div className="info-item">
                  <label>Organization</label>
                  <div className="info-value">
                    <Tag minimal>{auth.organization?.name || 'No Organization'}</Tag>
                  </div>
                </div>
                
                <div className="info-item">
                  <label>Role</label>
                  <div className="info-value">
                    <Tag minimal intent={user?.role === 'admin' ? Intent.SUCCESS : Intent.NONE}>
                      {user?.role || 'member'}
                    </Tag>
                  </div>
                </div>

                <div className="info-item">
                  <label>User ID</label>
                  <div className="info-value">
                    <code>{user?.id || 'N/A'}</code>
                  </div>
                </div>

                <div className="info-item">
                  <label>Status</label>
                  <div className="info-value">
                    <Tag minimal intent={Intent.SUCCESS}>Active</Tag>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="settings-card danger-card">
              <h3>Danger Zone</h3>
              <p>Irreversible and destructive actions</p>
              <div className="card-actions">
                <Button 
                  intent={Intent.DANGER}
                  icon={IconNames.LOG_OUT}
                  text="Sign Out"
                  onClick={() => auth.signOut()}
                />
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'admin' && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h2>Team Administration</h2>
              <p>Manage organization members</p>
              <Button intent={Intent.PRIMARY} icon={IconNames.PLUS} text="Invite Member" onClick={() => setInviteDialogOpen(true)} />
            </div>
            
            {statusMessage && (
              <div style={{ marginBottom: '15px' }}>
                <Tag large intent={statusMessage.intent}>
                  {statusMessage.text}
                </Tag>
              </div>
            )}
            {loadingMembers ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><Spinner size={40} /></div>
            ) : (
              <Card className="settings-card">
                <h3>Members ({members.length})</h3>
                {members.map((m) => {
                  const isPending = m.status === 'pending' || m.status === 'invited';
                  const isDeleted = !!m.isDeleted;
                  const isCurrentUser = m.email === user?.email;
                  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
                  
                  return (
                    <div 
                      key={m.id} 
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        padding: '12px', 
                        borderBottom: '1px solid #ddd',
                        backgroundColor: isDeleted ? '#f5f5f5' : 'transparent',
                        opacity: isDeleted ? 0.6 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%', 
                          background: isDeleted ? '#999' : (isPending ? '#999' : '#667eea'), 
                          color: 'white', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontWeight: 600,
                          opacity: (isPending || isDeleted) ? 0.6 : 1
                        }}>
                          {m.initials}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, opacity: (isPending || isDeleted) ? 0.7 : 1 }}>
                            {m.firstName || m.lastName ? `${m.firstName || ''} ${m.lastName || ''}`.trim() : m.email}
                            {isPending && !isDeleted && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999' }}>(Invite Pending)</span>}
                            {isDeleted && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999' }}>(Removed)</span>}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{m.email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select 
                          value={m.role} 
                          onChange={(e) => handleUpdateMemberRole(m.id, e.target.value)} 
                          style={{ padding: '6px' }}
                          disabled={isPending || isDeleted}
                          title="Select member role"
                          aria-label="Member role"
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        
                        {!isDeleted && (
                          <Tag intent={m.status === 'active' ? Intent.SUCCESS : Intent.WARNING}>
                            {m.status === 'active' ? 'active' : 'pending'}
                          </Tag>
                        )}
                        
                        {isDeleted && (
                          <Tag intent={Intent.NONE}>removed</Tag>
                        )}
                        
                        {isPending && !isDeleted && (
                          <Button 
                            small 
                            minimal 
                            icon={IconNames.ENVELOPE} 
                            intent={Intent.PRIMARY}
                            onClick={() => handleResendInvite(m.id)} 
                            title="Resend Invite"
                            text="Resend"
                          />
                        )}
                        
                        {/* Delete button - only for admins, only for active members, not yourself */}
                        {!isDeleted && isAdmin && !isCurrentUser && (
                          <Button 
                            small 
                            minimal 
                            icon={IconNames.TRASH} 
                            intent={Intent.DANGER}
                            onClick={() => handleDeleteMember(m)} 
                            title="Remove from organization"
                          />
                        )}
                        
                        {/* Restore button - only for admins, only for deleted members */}
                        {isDeleted && isAdmin && (
                          <Button 
                            small 
                            icon={IconNames.UNDO} 
                            intent={Intent.SUCCESS}
                            onClick={() => handleRestoreMember(m.id)} 
                            title="Restore member"
                            text="Restore"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}
            
            {/* Delete Confirmation Dialog */}
            <Dialog 
              isOpen={deleteConfirmOpen} 
              onClose={() => !deleting && setDeleteConfirmOpen(false)} 
              title="Remove Member"
              icon={IconNames.WARNING_SIGN}
            >
              <div className={Classes.DIALOG_BODY}>
                <Callout intent={Intent.WARNING} style={{ marginBottom: '16px' }}>
                  Are you sure you want to remove <strong>{memberToDelete?.email}</strong> from the organization?
                </Callout>
                <p style={{ marginBottom: '12px' }}>This will:</p>
                <ul style={{ marginLeft: '20px', marginBottom: '16px' }}>
                  <li>Revoke their access immediately</li>
                  <li>Mark them as removed in the system</li>
                  <li>Preserve their created content (mindmaps, tickets, etc.)</li>
                  <li>Allow you to restore them later if needed</li>
                </ul>
                <p style={{ fontSize: '12px', color: '#666' }}>
                  Note: This is a soft delete. You can restore this member from the Team Admin panel.
                </p>
              </div>
              <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                  <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button 
                    intent={Intent.DANGER} 
                    onClick={confirmDeleteMember} 
                    loading={deleting}
                    icon={IconNames.TRASH}
                  >
                    Remove Member
                  </Button>
                </div>
              </div>
            </Dialog>
            
            {/* Invite Dialog */}
            <Dialog isOpen={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} title="Invite Member">
              <div className={Classes.DIALOG_BODY}>
                <FormGroup label="Email"><InputGroup value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} leftIcon={IconNames.ENVELOPE} /></FormGroup>
                <FormGroup label="First Name"><InputGroup value={inviteFirstName} onChange={(e) => setInviteFirstName(e.target.value)} /></FormGroup>
                <FormGroup label="Last Name"><InputGroup value={inviteLastName} onChange={(e) => setInviteLastName(e.target.value)} /></FormGroup>
                <FormGroup label="Role">
                  <div className={Classes.SELECT}>
                    <select 
                      value={inviteRole} 
                      onChange={(e) => setInviteRole(e.target.value)} 
                      style={{ width: '100%' }}
                      title="Select role for new member"
                      aria-label="Member role"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </FormGroup>
              </div>
              <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                  <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
                  <Button intent={Intent.PRIMARY} onClick={handleInviteMember} loading={inviting} disabled={!inviteEmail}>Invite</Button>
                </div>
              </div>
            </Dialog>
          </div>
        )}

        {/* Debug Tab - Backend Logs */}
        {activeTab === 'debug' && (user?.role === 'admin' || user?.role === 'owner') && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h2>Debug Tools</h2>
              <p>Backend API logs and debugging tools</p>
            </div>
            <Card className="settings-card">
              <DebugLogsContent />
            </Card>
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * Inline debug logs content component
 * Shows backend API logs in the settings page
 */
const DebugLogsContent: React.FC = observer(() => {
  const [logs, setLogs] = React.useState<LogEntry[]>([]);
  const [expandedLogs, setExpandedLogs] = React.useState<Set<string>>(new Set());
  const logsEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setLogs(logCollector.getLogs());
    
    const unsubscribe = logCollector.subscribe((log) => {
      if (log.id === 'clear') {
        setLogs([]);
      } else {
        setLogs(prev => {
          const newLogs = [...prev, log];
          if (newLogs.length > 50) {
            newLogs.shift();
          }
          return newLogs;
        });
      }
    });

    return unsubscribe;
  }, []);

  React.useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const toggleLogExpansion = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getMethodColor = (method: string): Intent => {
    switch (method) {
      case 'GET': return Intent.PRIMARY;
      case 'POST': return Intent.SUCCESS;
      case 'PUT': return Intent.WARNING;
      case 'PATCH': return Intent.WARNING;
      case 'DELETE': return Intent.DANGER;
      default: return Intent.NONE;
    }
  };

  const getStatusColor = (status?: number): Intent => {
    if (!status) return Intent.NONE;
    if (status >= 200 && status < 300) return Intent.SUCCESS;
    if (status >= 300 && status < 400) return Intent.WARNING;
    if (status >= 400 && status < 500) return Intent.DANGER;
    if (status >= 500) return Intent.DANGER;
    return Intent.NONE;
  };

  const formatDuration = (duration?: number): string => {
    if (!duration) return '';
    if (duration < 1000) return `${duration}ms`;
    return `${(duration / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp: Date): string => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  return (
    <div style={{ minHeight: '300px', maxHeight: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Header with clear button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #e1e8ed'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon icon={IconNames.CONSOLE} />
          <strong>Backend API Logs</strong>
          <Tag minimal>{logs.length}</Tag>
        </div>
        <Button
          icon={IconNames.TRASH}
          text="Clear Logs"
          small
          onClick={() => logCollector.clear()}
        />
      </div>

      {/* Logs content */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto', 
        border: '1px solid #e1e8ed', 
        borderRadius: '4px',
        background: '#f7f8fa',
        padding: '8px'
      }}>
        {logs.length === 0 ? (
          <div style={{ 
            padding: '32px', 
            textAlign: 'center', 
            color: '#738091',
            fontStyle: 'italic' 
          }}>
            No logs yet...
          </div>
        ) : (
          logs.map(log => (
            <div 
              key={log.id} 
              style={{
                marginBottom: '4px',
                border: log.error ? '1px solid #db3737' : '1px solid #e1e8ed',
                borderRadius: '4px',
                background: log.error ? '#fff5f5' : 'white',
                transition: 'all 0.2s ease'
              }}
            >
              <div 
                onClick={() => toggleLogExpansion(log.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <span style={{
                  fontFamily: 'Monaco, Menlo, monospace',
                  fontSize: '11px',
                  color: '#738091',
                  minWidth: '80px'
                }}>
                  {formatTimestamp(log.timestamp)}
                </span>
                <Tag 
                  intent={getMethodColor(log.method)}
                  minimal
                  style={{ minWidth: '60px', textAlign: 'center', fontWeight: 600 }}
                >
                  {log.method}
                </Tag>
                <span style={{
                  flex: 1,
                  fontFamily: 'Monaco, Menlo, monospace',
                  fontSize: '12px',
                  color: '#182026',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {log.url}
                </span>
                {log.status && (
                  <Tag 
                    intent={getStatusColor(log.status)}
                    minimal
                    style={{ minWidth: '40px', textAlign: 'center' }}
                  >
                    {log.status}
                  </Tag>
                )}
                {log.duration && (
                  <span style={{
                    fontFamily: 'Monaco, Menlo, monospace',
                    fontSize: '11px',
                    color: '#738091',
                    minWidth: '60px',
                    textAlign: 'right'
                  }}>
                    {formatDuration(log.duration)}
                  </span>
                )}
                <Icon 
                  icon={expandedLogs.has(log.id) ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN}
                  style={{ color: '#738091', transition: 'transform 0.2s ease' }}
                />
              </div>
              
              {expandedLogs.has(log.id) && (
                <div style={{
                  padding: '12px',
                  borderTop: '1px solid #e1e8ed',
                  background: '#f7f8fa'
                }}>
                  {log.request && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{
                        fontWeight: 600,
                        color: '#182026',
                        marginBottom: '8px',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Request:
                      </div>
                      <pre style={{
                        fontFamily: 'Monaco, Menlo, monospace',
                        fontSize: '11px',
                        background: 'white',
                        border: '1px solid #d3d8de',
                        borderRadius: '4px',
                        padding: '8px',
                        margin: 0,
                        overflowX: 'auto',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        color: '#182026'
                      }}>
                        {JSON.stringify(log.request, null, 2)}
                      </pre>
                    </div>
                  )}
                  {log.response && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{
                        fontWeight: 600,
                        color: '#182026',
                        marginBottom: '8px',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Response:
                      </div>
                      <pre style={{
                        fontFamily: 'Monaco, Menlo, monospace',
                        fontSize: '11px',
                        background: 'white',
                        border: '1px solid #d3d8de',
                        borderRadius: '4px',
                        padding: '8px',
                        margin: 0,
                        overflowX: 'auto',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        color: '#182026'
                      }}>
                        {JSON.stringify(log.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  {log.error && (
                    <div style={{ marginBottom: 0 }}>
                      <div style={{
                        fontWeight: 600,
                        color: '#182026',
                        marginBottom: '8px',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Error:
                      </div>
                      <pre style={{
                        fontFamily: 'Monaco, Menlo, monospace',
                        fontSize: '11px',
                        background: '#fff5f5',
                        border: '1px solid #db3737',
                        borderRadius: '4px',
                        padding: '8px',
                        margin: 0,
                        overflowX: 'auto',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        color: '#db3737'
                      }}>
                        {typeof log.error === 'string' ? log.error : JSON.stringify(log.error, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
});

export default SettingsPage;